import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LModal } from "@components";
import { useApi } from "@services/useApi";
import { NewAssessmentBody, NewAssessmentFooter, NoUpcomingAssessments } from "../components";
import { CalendarFooter } from "../components/CalendarFooter";
import { ExistingFooter } from "../components/existing-assessment/ExistingFooter";
import { ExamineesHeader } from "../components/ExamineesHeader";
import { UpcomingCalendar } from "../components/new-assessment-body/UpcomingCalendar";
import { NewExamineeStepNames, resetExaminee, setExamineeId } from "../store/newExamineeSlice";
import { ExistingExaminees } from "../components/existing-assessment/ExistingExaminees";
import { updateCompletedAssessments, updateUpcomingAssessments } from "@store/locationsSlice";
import { UpcomingAssessments } from "../components/upcoming/UpcomingAssessments";
import { AssessmentStatus } from "@store/generalTypes";
import { Loader } from "@components/Loader";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { StartAssessmentModal } from "../components/right-side/start-assessment/StartAssessmentModal";
import { MissingRolesModal } from "@fadmin/components/missingRolesModal";
import { useNavigate } from "react-router-dom";
import { NavItems } from "@helpers/navigation";

const formSteps = [NewExamineeStepNames.ContactInfo, NewExamineeStepNames.Details, NewExamineeStepNames.AssignedTo];

export const AdminAssessmentsUpcoming = (): JSX.Element => {
    const [newAssessmentOpen, setNewAssessmentOpen] = useState(false);
    const [existingAssessmentOpen, setExistingAssessmentOpen] = useState(false);
    const [beginAssessmentOpen, setBeginAssessmentOpen] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [calendarValue, setCalendarValue] = useState(new Date());
    const [getDataLoading, setGetDataLoading] = useState(false);
    const [upcomingAssessmentId, setUpcomingAssessmentId] = useState<number | undefined>(undefined);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { addExaminee, loading, setAppointment, getUpcomingAssessments, getAssessments } = useApi();

    const { examinee, upcomingAssessments, currentLocation, organizationAssessments } = useAppSelector((x) => ({
        examinee: x.newExaminee,
        upcomingAssessments: x.orgLocations.upcomingAssessments,
        currentLocation: x.orgLocations.currentLocation,
        organizationAssessments: x.organizationAssessments
    }));

    const { isClinicalAdmin, isFacilitator } = useUserPermission({ useCurrentLocation: true });
    const canCreate = isClinicalAdmin || isFacilitator;

    const resetValues = (): void => {
        dispatch(resetExaminee());
        setCurrentStep(0);
        setCalendarValue(new Date());
    };

    const handleNewContinue = async (): Promise<void> => {
        const nextCurrentStep = currentStep + 1;

        if (!formSteps[nextCurrentStep]) {
            const examineeIds = await addExaminee(examinee.assignedTo!.location, examinee);
            dispatch(setExamineeId(examineeIds));

            setCalendarOpen(true);
            setNewAssessmentOpen(false);
        } else {
            setCurrentStep(nextCurrentStep);
        }
    };

    const handleCalendarConfirm = async (): Promise<void> => {
        const appointmentId = (
            await setAppointment(examinee.assignedTo!.location, Number(examinee.ids!.id), calendarValue)
        ).id;
        setUpcomingAssessmentId(appointmentId);

        await handleGetUpcomingAssessments();
        resetValues();
        toggleCalendar();
    };

    const toggleCalendar = (): void => {
        setCalendarOpen(!calendarOpen);
    };

    const toggleNewAssessment = (): void => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            setCurrentStep(0);
            dispatch(resetExaminee());

            setNewAssessmentOpen(!newAssessmentOpen);
        }
    };

    const toggleExistingAssessment = (): void => {
        setExistingAssessmentOpen(!existingAssessmentOpen);
    };

    const toggleBeginAssessment = (): void => {
        setExistingAssessmentOpen(false);
        setBeginAssessmentOpen(!beginAssessmentOpen);
    };

    const handleChangeCalendarValue = (date: Date): void => {
        setCalendarValue(date);
    };

    const handleGoToExistingExaminee = (): void => {
        dispatch(resetExaminee());
        setCurrentStep(0);
        setCalendarValue(new Date());
        toggleNewAssessment();
        toggleExistingAssessment();
    };

    const handleGoToNewExaminee = (): void => {
        resetValues();
        toggleExistingAssessment();
        toggleNewAssessment();
    };

    const handleSelectExistingExaminee = (): void => {
        setCalendarOpen(true);
        setExistingAssessmentOpen(false);
    };

    const handleGetUpcomingAssessments = async (): Promise<void> => {
        setGetDataLoading(true);
        const assessments = await getUpcomingAssessments(currentLocation!.id).finally(() => {
            setGetDataLoading(false);
        });

        dispatch(updateUpcomingAssessments(assessments));
    };

    const handleGetCompletedAssessments = async (): Promise<void> => {
        const assessments = await getAssessments(currentLocation!.id, false, AssessmentStatus.Complete);

        dispatch(updateCompletedAssessments(assessments));
    };

    useEffect(() => {
        void handleGetUpcomingAssessments();
        void handleGetCompletedAssessments();
    }, [currentLocation?.id]);

    const handlerOnNewAssessment = (): void => {
        if (canCreate) {
            toggleNewAssessment();
        } else {
            toggleExistingAssessment();
        }
    };

    const handleOnMissingRolesModalClose = (): void => {
        navigate(NavItems.Admin.Manage.parentRoot);
    };

    return (
        <>
            {getDataLoading ? (
                <Loader />
            ) : upcomingAssessments?.length ? (
                <UpcomingAssessments
                    assessments={upcomingAssessments}
                    disableButtons={!organizationAssessments.hasAssessment}
                    onClick={handlerOnNewAssessment}
                />
            ) : (
                <NoUpcomingAssessments
                    disableButtons={!organizationAssessments.hasAssessment}
                    onClick={handlerOnNewAssessment}
                />
            )}
            {/* NEW EXAMINEE */}
            {newAssessmentOpen && (
                <LModal
                    onClose={toggleNewAssessment}
                    onConfirm={handleNewContinue}
                    footer={<NewAssessmentFooter loading={loading} />}
                    header={
                        <ExamineesHeader
                            isNew
                            allowCreateNew={canCreate}
                            onClickExisting={handleGoToExistingExaminee}
                        />
                    }
                    width={currentStep === 1 ? "l" : "m"}
                    fullHeight={currentStep === 1}
                >
                    <NewAssessmentBody currentStep={formSteps[currentStep]} />
                </LModal>
            )}

            {/* EXISTING EXAMINEE */}
            {existingAssessmentOpen && (
                <LModal
                    onClose={toggleExistingAssessment}
                    onConfirm={handleSelectExistingExaminee}
                    footer={
                        examinee.ids ? (
                            <ExistingFooter loading={loading} onBeginAssessment={toggleBeginAssessment} />
                        ) : (
                            <></>
                        )
                    }
                    width={"m"}
                    fullHeight={false}
                    header={
                        <ExamineesHeader isNew={false} allowCreateNew={canCreate} onClickNew={handleGoToNewExaminee} />
                    }
                    hasCloseButton
                >
                    <ExistingExaminees />
                </LModal>
            )}

            {/* CALENDAR */}
            {calendarOpen && (
                <LModal
                    onClose={toggleCalendar}
                    onConfirm={handleCalendarConfirm}
                    footer={<CalendarFooter loading={loading} />}
                    fullHeight={false}
                    title={
                        <p className="has-text-weight-bold is-size-3 mx-5 has-text-centered">Select Assessment Date</p>
                    }
                >
                    <div className="is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
                        <p className="mb-3 has-text-ldark-gray">Optional</p>
                        <UpcomingCalendar value={calendarValue} onChange={handleChangeCalendarValue} />
                    </div>
                </LModal>
            )}

            {/* START ASSESSMENT */}
            {beginAssessmentOpen && (
                <StartAssessmentModal
                    examinee={examinee.contactInfo}
                    locationId={Number(currentLocation?.id)}
                    upcomingAssessmentId={upcomingAssessmentId}
                    onClose={toggleBeginAssessment}
                />
            )}
            <MissingRolesModal onModalClose={handleOnMissingRolesModalClose} />
        </>
    );
};
